import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { TextFieldModule } from "@angular/cdk/text-field";

import swal from "sweetalert2";
import { MatrizRiesgoService } from "../matriz-riesgo.service";
import { NOTAS_EXCEL } from "../notas-excel";
import {
  CatAmenaza,
  CatProbabilidad,
  CatValoracion,
  MatrizActivo,
  MatrizAmenaza,
  MatrizVulnerabilidad,
} from "src/app/shared/model/iso42001/matriz-riesgo.model";
import { LIMITES } from "../limites";
import { avisarPropuesta, quedoComoPropuesta } from "../propuesta";

/**
 * Alta y edición de un activo de la matriz. Igual que el inventario, no es un
 * MatDialog: es una pantalla completa ruteada, y el mismo componente sirve
 * ambos casos leyendo el :id de la ruta.
 *
 * La captura es anidada: activo -> amenaza -> vulnerabilidad -> acción. Esa
 * jerarquía es la que en el Excel se sostenía sólo por la posición de la fila.
 */
@Component({
  selector: "app-modal-matriz-riesgos-ia",
  standalone: true,
  templateUrl: "./modal-matriz-riesgos-ia.component.html",
  styleUrls: ["./modal-matriz-riesgos-ia.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatNativeDateModule,
    TextFieldModule,
  ],
})
export class ModalMatrizRiesgosIAComponent implements OnInit {

  /**
   * Límites de captura, tomados del largo real de cada columna. Van al
   * maxlength de los campos: producción corre en modo estricto y un texto más
   * largo que su columna aborta el guardado en vez de truncarse.
   */
  lim = LIMITES;

  activo: MatrizActivo = {
    nombreActivo: "",
    tipoActivo: "Software",
    amenazas: [],
  };

  valoraciones: CatValoracion[] = [];
  probabilidades: CatProbabilidad[] = [];
  catAmenazas: CatAmenaza[] = [];

  edicion = false;
  guardando = false;
  cargando = false;

  /**
   * Id del activo cuando se usa embebido dentro de otra pantalla. Si no viene,
   * el componente se comporta como pantalla propia y lo toma de la ruta.
   */
  @Input() idActivo: number | null = null;

  /** true cuando se abre dentro del panel de la matriz, no como ruta. */
  /**
   * A qué matriz pertenece el activo que se está capturando. Sin este dato
   * el alta caería en la matriz 1 (IA) aunque se hubiera abierto desde otra.
   */
  @Input() idMatriz = 1;

  @Input() embebido = false;

  /** Avisa al padre que hay que cerrar. true = se guardó, hay que recargar. */
  @Output() cerrado = new EventEmitter<boolean>();

  /** Notas del Excel: salen al pasar el mouse por el campo. */
  notas = NOTAS_EXCEL;

  constructor(
    private service: MatrizRiesgoService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargarCatalogos();
    const id = this.idActivo || this.route.snapshot.paramMap.get("id");
    if (id) {
      this.edicion = true;
      this.cargar(Number(id));
    }
  }

  /**
   * Modo lectura. Arranca en true para no parpadear un aviso a quien sí
   * puede editar; lo corrige el servidor en cuanto contestan los catálogos,
   * que es la primera llamada tanto del alta como de la edición. El candado
   * de verdad está en el backend: esto sólo evita que alguien capture un
   * activo entero para enterarse al final de que no lo podía guardar.
   */
  puedeEditar = true;

  private cargarCatalogos(): void {
    this.service.catalogos().subscribe({
      next: (res) => {
        this.puedeEditar = res.puedeEditar === true;
        this.valoraciones = res.valoraciones || [];
        this.probabilidades = res.probabilidades || [];
        this.catAmenazas = res.catAmenazas || [];
        this.cdr.detectChanges();
      },
      error: (err) => console.error("Error al cargar catálogos", err),
    });
  }

  private cargar(id: number): void {
    this.cargando = true;
    this.cdr.detectChanges();
    this.service.obtener(id).subscribe({
      next: (res) => {
        if (res.activo) {
          this.activo = res.activo;
          if (!this.activo.amenazas) {
            this.activo.amenazas = [];
          }
        }
        this.cargando = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargando = false;
        this.cdr.detectChanges();
        swal.fire("Error", "No se pudo cargar el activo.", "error");
      },
    });
  }

  /* ---------- Catálogos filtrados para los desplegables ---------- */

  valoracionPor(dimension: string): CatValoracion[] {
    return this.valoraciones.filter((v) => v.dimension === dimension);
  }

  probabilidadPor(aplicaA: string): CatProbabilidad[] {
    return this.probabilidades.filter((p) => p.aplicaA === aplicaA);
  }

  get categoriasAmenaza(): string[] {
    const vistas: string[] = [];
    for (const a of this.catAmenazas) {
      if (a.categoria && vistas.indexOf(a.categoria) === -1) {
        vistas.push(a.categoria);
      }
    }
    return vistas;
  }

  amenazasDe(categoria: string): CatAmenaza[] {
    return this.catAmenazas.filter((a) => a.categoria === categoria);
  }

  /** Valor del activo = máximo de C, I y D. Se muestra en vivo; el backend
   *  lo recalcula al guardar, así que esto es sólo apoyo visual. */
  get valorActivoPreview(): number | null {
    const vals = [
      this.activo.valConfidencialidad,
      this.activo.valIntegridad,
      this.activo.valDisponibilidad,
    ].filter((v) => v !== null && v !== undefined) as number[];
    return vals.length ? Math.max(...vals) : null;
  }

  /* ---------- Manejo del árbol ---------- */

  agregarAmenaza(): void {
    this.activo.amenazas = this.activo.amenazas || [];
    this.activo.amenazas.push({
      nombreAmenaza: "",
      probActual: null,
      probTrasPlan: null,
      vulnerabilidades: [],
    });
  }

  quitarAmenaza(i: number): void {
    this.activo.amenazas?.splice(i, 1);
  }

  agregarVulnerabilidad(am: MatrizAmenaza): void {
    am.vulnerabilidades = am.vulnerabilidades || [];
    am.vulnerabilidades.push({
      nombreVulnerabilidad: "",
      probActual: null,
      probTrasPlan: null,
      acciones: [],
    });
  }

  quitarVulnerabilidad(am: MatrizAmenaza, i: number): void {
    am.vulnerabilidades?.splice(i, 1);
  }

  agregarAccion(vu: MatrizVulnerabilidad): void {
    vu.acciones = vu.acciones || [];
    vu.acciones.push({
      descripcion: "",
      estado: "Programada",
      responsable: "",
      prioridad: "Media",
      recursos: "",
    });
  }

  quitarAccion(vu: MatrizVulnerabilidad, i: number): void {
    vu.acciones?.splice(i, 1);
  }

  /* ---------- Guardar ---------- */

  /**
   * Recorre el árbol buscando el primer campo obligatorio vacío y devuelve el
   * aviso, o cadena vacía si está todo.
   *
   * Hace falta porque nombre_amenaza, nombre_vulnerabilidad y la descripción
   * de la acción son NOT NULL en la base: sin esta comprobación el guardado
   * viaja, el backend lo rechaza y el usuario ve un error genérico sin saber
   * cuál de los renglones del árbol es el que falta.
   */
  private primeroSinCapturar(): string {
    const amenazas = this.activo.amenazas || [];
    for (let i = 0; i < amenazas.length; i++) {
      const am = amenazas[i];
      if (!am.nombreAmenaza || !am.nombreAmenaza.trim()) {
        return "La amenaza " + (i + 1) + " no tiene nombre.";
      }
      const vulns = am.vulnerabilidades || [];
      for (let j = 0; j < vulns.length; j++) {
        const vu = vulns[j];
        if (!vu.nombreVulnerabilidad || !vu.nombreVulnerabilidad.trim()) {
          return "La vulnerabilidad " + (j + 1) + " de la amenaza " + (i + 1) + " no tiene nombre.";
        }
        const accs = vu.acciones || [];
        for (let k = 0; k < accs.length; k++) {
          if (!accs[k].descripcion || !accs[k].descripcion!.trim()) {
            return "La acción " + (k + 1) + " de la vulnerabilidad " + (j + 1) +
              " (amenaza " + (i + 1) + ") no tiene descripción.";
          }
        }
      }
    }
    return "";
  }

  guardar(): void {
    if (!this.puedeEditar) {
      swal.fire(
        "Sólo lectura",
        "La matriz es un documento certificable: sólo el perfil de edición puede cambiarla.",
        "info"
      );
      return;
    }
    if (!this.activo.nombreActivo || !this.activo.nombreActivo.trim()) {
      swal.fire("Falta información", "El nombre del activo es obligatorio.", "warning");
      return;
    }
    const falta = this.primeroSinCapturar();
    if (falta) {
      swal.fire("Falta información", falta, "warning");
      return;
    }

    this.guardando = true;
    this.cdr.detectChanges();

    // El alta se estampa con la matriz desde la que se abrió el editor; al
    // editar se respeta la que ya trae el activo.
    if (!this.edicion) {
      this.activo.idMatriz = this.idMatriz;
    }

    const llamada = this.edicion
      ? this.service.actualizar(this.activo)
      : this.service.guardar(this.activo);

    llamada.subscribe({
      next: (res) => {
        this.guardando = false;
        this.cdr.detectChanges();
        // Cuerpo vacío también es falla: el backend responde 200 aunque truene.
        if (!res || res.codigoError) {
          swal.fire("No se guardó", (res && res.mensajeError) || "Error desconocido.", "warning");
          return;
        }
        // Con el control de cambios encendido no se guardó: quedó propuesto.
        // Se cierra igual y la matriz relee, que sigue como estaba.
        if (quedoComoPropuesta(res)) {
          void avisarPropuesta(this.edicion
            ? "El cambio de " + (this.activo.consecutivo || "este activo")
            : "El alta del activo").then(() => this.salir(true));
          return;
        }
        swal
          .fire("Listo", "El activo se guardó correctamente.", "success")
          .then(() => this.salir(true));
      },
      error: (err) => {
        console.error("Error al guardar el activo de la matriz", err);
        this.guardando = false;
        this.cdr.detectChanges();
        swal.fire("Error", "No se pudo guardar el activo.", "error");
      },
    });
  }

  regresar(): void {
    this.salir(false);
  }

  /**
   * Como pantalla propia regresa al listado; embebido solo avisa al padre, que
   * es quien decide si recarga la matriz y cierra el panel.
   */
  private salir(guardado: boolean): void {
    if (this.embebido) {
      this.cerrado.emit(guardado);
      return;
    }
    this.router.navigate(["/admin/matriz-riesgos-ia/gestion"]);
  }
}
